import { forwardRef, useEffect, useState, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import { StepHeaderEntity } from "../../../../../entities/UIEntities/Form Entities/StepHeaderEntity";
import { ListItem } from "../../../../../entities/UIEntities/ListItem";
import {
  ADD_BILLING_RULE_FORM_STEPS,
  INPUTS_PROPS,
} from "../../../../../enums/Enums";
import { commonActions } from "../../../../../redux/reducers/CommonReducer";
import store from "../../../../../redux/Store";
import Icons from "../../../../../resources/icons/Icons";
import Strings from "../../../../../resources/Strings";
import {
  convertCurrencyToSymbol,
  createGuid,
  timeStringToMins, // added
  minutesToString // added
} from "../../../../../utils/CommonUtils";
import { getRuleDurationComparisonWithRates, getStartTimeFromDate } from "../../../../../utils/BillingUtils";
import {
  /*arrayNotEmpty,*/
  getErrorMessage,
  isRequired,
} from "../../../../../utils/FormUtils";
import TextInput from "../../../Inputs/CustomInputs/TextInput";
import classes from "../WizardForm.module.css";
import { CHARGE_BY_LIST } from "../../../../../constants/Constants";
import SelectInput from "../../../Inputs/CustomInputs/SelectInput";
import EllipseButton from "../../../Buttons/EllipseButton/EllipseButton";
import CustomTooltip from "../../../Tooltip/CustomTooltip/CustomTooltip";



const AddRateStep = forwardRef((props, ref) => {
  const [errors, setErrors] = useState({});

  const selectedSites = useSelector((state) => state.user.selectedSites);
  const selectedSite = props.selectedSite;
  
  //const selectedSite = useSelector((state) => state.user.selectedSite);
  const [stepHeader, setStepHeader] =  useState(new StepHeaderEntity(
    props.isEditMode
      ? Strings.STEPS_TITLES.EDIT_BILLING_RULE_MODAL_TITLE
      : Strings.STEPS_TITLES.ADD_BILLING_RULE_MODAL_TITLE,
    "",
    Icons.BILLING_RULE,
    null
  ));

  // added for periods to show on selection
  const [periodNumber, setPeriodNumber] = useState(0); 
  const [stepData, setStepData] = useState({
    id: "",
    rateAmount: "",
    chargeBy: "",
    duration: 0,
    period: "", 
    currency: convertCurrencyToSymbol(selectedSite.billingCurrency),
  });

  const resetRateFields = () => {
    setStepData({id: "",
    rateAmount: "",
    chargeBy: "",
    duration: 0,
    period: "",
    currency: convertCurrencyToSymbol(selectedSite.billingCurrency)});
  };

  useImperativeHandle(ref, () => ({
    resetRateFields,
    setPeriodNumber
  }));
  
  useEffect(() => {
    store.dispatch(commonActions.setSideModal({ modalHeader: stepHeader }));
  }, [stepHeader])
  
  useEffect(() => {
    if (props.currentStep === ADD_BILLING_RULE_FORM_STEPS.ADD_RATE) {
      const hasError = Object.values(errors).some((hasError) => hasError);
      props.handleCurrentStepValidity(hasError);
    }
  }, [errors]);
  if (props.currentStep !== ADD_BILLING_RULE_FORM_STEPS.ADD_RATE) {
    return null;
  }


  const handleChange = (newProp) => {
    setStepData({ ...stepData, ...newProp });
  };

  const changePeriodSelection = (newProp) => {
    let totalDurationMinutes = getRuleDurationComparisonWithRates(props?.startTime, props?.endTime, props?.rates, props?.isFullDay);
    
    let startTime = getStartTimeFromDate(props?.startTime)//props?.startTime.toLocaleTimeString('en-US', { hour12: false  });
    let endTime = props?.isFullDay ? "24:00:00" : props?.endTime.toLocaleTimeString('en-US', { hour12: false  });
    let ruleDuration = minutesToString( timeStringToMins(endTime) - timeStringToMins(startTime));
    let hours = parseInt(ruleDuration.split(':')[0]);
    let minutes = parseInt(ruleDuration.split(':')[1]);

    // Deal with selection - Periods to show by switching to Hours / Minutes
    let chargeBySelection = newProp?.chargeBy?.name;
    let numberOfHoursToShow = Math.floor(totalDurationMinutes / 60);
    let numberOfMinutesToShow = totalDurationMinutes / 30;

    if (chargeBySelection === "Hours" && hours !== 0 && minutes === 0) {
      setPeriodNumber(numberOfHoursToShow); // hours
    } else if (chargeBySelection === "Minutes" && hours !== 0 && minutes === 0) {
      // Minutes
      setPeriodNumber(numberOfMinutesToShow); // (hours * 2)
    }

    // With half hour
    if (chargeBySelection === "Hours" && hours !== 0 && minutes !== 0) {
      setPeriodNumber(numberOfHoursToShow);
    } else if (chargeBySelection === "Minutes" && hours !== 0 && minutes !== 0){
      setPeriodNumber(numberOfMinutesToShow); // (hours * 2) + 1)
    }

    // Only half hour
    if (chargeBySelection === "Hours" && hours === 0 && minutes !== 0) {
      setPeriodNumber(numberOfHoursToShow); // 0
    } else if (chargeBySelection === "Minutes" && hours === 0 && minutes !== 0) {
      setPeriodNumber(numberOfMinutesToShow); // 1
    }
    
    //setPeriodNumber(periodNumber + 1);
    handleChange(newProp)
  };

  const handleError = (error) => {
    setErrors((prevState) => ({
      ...prevState,
      ...error,
    }));
  };

// Prepare a check for hiding 'Add new rate' button before returning to the AddBillingRule step 
const getSelectedDurationInMinutes = () => {
  let selectedDurationInMinutes = 0;
  let selectionName = stepData?.chargeBy;
  if (selectionName === "Hours") {
    selectedDurationInMinutes = stepData?.period * 60;
  }
  else {
    selectedDurationInMinutes = stepData?.period * 30;
  }
  
  return selectedDurationInMinutes;
};

  const handleAddRate = () => {
    if (props.isCurrentStepValid) {
      const ratesCopy = [...props.rates];
      stepData["id"] = createGuid();
      stepData["duration"] = stepData.chargeBy.duration 
      stepData["chargeBy"] = stepData.chargeBy.name; 
      ratesCopy.push(stepData);
      props.handleChange({ [INPUTS_PROPS.RATES]: ratesCopy });
      props.prev();
      props.isRuleFilled(getSelectedDurationInMinutes());
      setPeriodNumber(0);
      resetRateFields();
    }
  };
  


  return (
      <div className={classes.stepContainer}>
        <div className={classes.stepTitle}>
          {Strings.STEPS_TITLES.ADD_RATE_TITLE}
          <CustomTooltip title={""} tooltipTitle={Strings.TOOLTIP_INFO_TITLE.ADD_RATE_LINE} tooltipText={Strings.TOOLTIP_INFO_TEXT.ADD_RATE_LINE}  iconPath={Icons.INFO_CIRCLE}></CustomTooltip>
        </div>
        <span className={classes.stepSubTitle}>
          {Strings.STEPS_TITLES.ADD_RATE_SUB_TITLE}
        </span>

        <div className={classes.fileds}>
          <TextInput
            label={Strings.INPUT_LABELS.RATE_AMOUNT}
            id={INPUTS_PROPS.RATE_AMOUNT}
            value={stepData.rateAmount}
            onChange={handleChange}
            isStepSubmitted={props.isStepSubmitted}
            handleError={handleError}
            hasError={!isRequired(stepData.rateAmount).isValid}
            errorMessage={getErrorMessage(isRequired(stepData.rateAmount))}
            startAdornment={stepData.currency}
            type={"number"}
          />
          <SelectInput
            label={Strings.INPUT_LABELS.CHARGE_BY}
            list={CHARGE_BY_LIST}
            id={INPUTS_PROPS.CHARGE_BY}
            value={stepData.chargeBy}
            onChange={changePeriodSelection}
            isStepSubmitted={props.isStepSubmitted}
            handleError={handleError}
            hasError={!isRequired(stepData.chargeBy).isValid}
            errorMessage={getErrorMessage(isRequired(stepData.chargeBy))}
          />
          <SelectInput
            label={Strings.INPUT_LABELS.NUMBER_OF_PERIODS}
            list={Array.from(Array(periodNumber).keys()).map(
              (value) => new ListItem(value, value + 1, value + 1)
            )}
            id={INPUTS_PROPS.NUMBER_OF_PERIODS}
            value={stepData.period}
            onChange={handleChange}
            isStepSubmitted={props.isStepSubmitted}
            handleError={handleError}
            hasError={!isRequired(stepData.period).isValid}
            errorMessage={getErrorMessage(isRequired(stepData.period))}
          />
        </div>
        <div className={classes.next}>
          <EllipseButton onClick={handleAddRate}>
            {Strings.CREATE_RATE}
          </EllipseButton>
        </div>
      </div>
  );
}); // added ) - for forward ref

export default AddRateStep;
